import type { Locale } from "./translations";

/**
 * Locale-aware internal URLs. This is the single place that decides whether a
 * path gets a locale prefix; it is deliberately conservative and returns the
 * path unchanged when in doubt.
 *
 * Every internal link on /en and /da must go through this helper: an unprefixed
 * link sends crawlers back into the German version, leaving the EN/DA clusters
 * without internal links even though hreflang, canonical and sitemap are correct.
 */

/**
 * Paths that never get a locale prefix: legal texts are German-only (a
 * /en/impressum would serve German content under an English URL), /admin is
 * German-only and /api routes are not pages.
 */
export const NEVER_PREFIXED = [
  "/admin",
  "/api",
  "/impressum",
  "/datenschutz",
  "/agb",
  "/widerruf",
  "/vollmacht",
];

/**
 * Returns the locale-correct path, preserving query and hash. German is the
 * unprefixed default; external, mailto/tel and already prefixed paths are
 * returned unchanged.
 */
export function localePath(path: string, locale: Locale): string {
  if (locale === "de") return path;
  // External links, mailto:, tel:, bare anchors and protocol-relative URLs.
  if (!path.startsWith("/") || path.startsWith("//")) return path;
  // Already prefixed (e.g. hand-written /en/... links).
  if (path === `/${locale}` || path.startsWith(`/${locale}/`)) return path;

  const hashIndex = path.indexOf("#");
  const queryIndex = path.indexOf("?");
  const cut = [hashIndex, queryIndex].filter((i) => i >= 0).sort((a, b) => a - b)[0];
  const pathname = cut === undefined ? path : path.slice(0, cut);
  const suffix = cut === undefined ? "" : path.slice(cut);

  if (NEVER_PREFIXED.some((p) => pathname === p || pathname.startsWith(`${p}/`))) {
    return path;
  }

  // Home page: "/" + "#faq" must not become "/en/#faq".
  if (pathname === "/" || pathname === "") return `/${locale}${suffix}`;

  return `/${locale}${pathname}${suffix}`;
}
